import { Link } from "react-router-dom";
import { Priority } from "@/types";
import { useCandidates } from "@/hooks/useCandidates";
import { daysSince } from "@/components/okr/okr-dates";
import { cn } from "@/lib/utils";

/** No DRI = no assignment flagged `isDri`; no PPP = no linked PPP at all. */
function missingLabel(p: Priority): string {
  const noDri = !p.dri && !p.assignments?.some((a) => a.isDri);
  const noPpp = p.ppps.length === 0;
  if (noDri && noPpp) return "no DRI, no PPP";
  if (noDri) return "no DRI";
  if (noPpp) return "no PPP";
  return "—";
}

/** Candidates (no DRI or no PPP) sorted oldest first; missing `createdAt` sorts last. */
export function CandidateAgingSection() {
  const { data, isLoading, isError } = useCandidates();

  if (isLoading) {
    return <div className="h-40 animate-pulse rounded-xl border border-border bg-muted/30" />;
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center text-sm text-destructive">
        Failed to load candidates.
      </div>
    );
  }

  const candidates = data?.data ?? [];
  const sorted = [...candidates].sort((a, b) => {
    const ageA = daysSince(a.createdAt);
    const ageB = daysSince(b.createdAt);
    if (ageA === null) return ageB === null ? 0 : 1;
    if (ageB === null) return -1;
    return ageB - ageA;
  });

  if (sorted.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 p-8 text-center">
        <p className="text-sm font-medium text-muted-foreground">No candidates waiting.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <ul className="divide-y divide-border/40 rounded-xl border border-border bg-card">
        {sorted.map((p) => {
          const age = daysSince(p.createdAt);
          return (
            <li key={p.pid}>
              <Link to={`/priorities/${p.pid}`} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted/40">
                <span className="shrink-0 font-mono text-xs text-muted-foreground">{p.code}</span>
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                <span className={cn("shrink-0 text-xs tabular-nums", age !== null && age >= 7 ? "font-medium text-amber-600 dark:text-amber-500" : "text-muted-foreground")}>
                  {age === null ? "—" : `${age}d`}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">{missingLabel(p)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      <Link to="/priorities?candidate=1" className="self-end text-xs text-muted-foreground hover:text-foreground hover:underline">
        View all
      </Link>
    </div>
  );
}
